import { Fragment } from 'react'
import { cn } from 'src/utils/style'

/** A run of template body text: either literal copy or a `{{variable}}` token. */
export interface TemplateBodySegment {
  text: string
  isVariable: boolean
}

// Matches a WhatsApp `{{ named }}` variable token (whitespace around the name is
// tolerated). The `g` form splits the body on tokens; the anchored form tags a
// segment as a variable — kept separate because `.test()` on a `/g` regex is
// stateful and would misclassify across calls.
const VARIABLE_SPLIT_PATTERN = /(\{\{\s*[\w.]+\s*\}\})/g
const VARIABLE_EXACT_PATTERN = /^\{\{\s*[\w.]+\s*\}\}$/

/**
 * Split a template body into literal and `{{variable}}` segments, in order.
 * Pure so it can be unit-tested without rendering. Consecutive text between
 * tokens stays a single segment; an empty body yields an empty list.
 */
export function splitTemplateBody(body: string): TemplateBodySegment[] {
  if (!body) return []
  return body
    .split(VARIABLE_SPLIT_PATTERN)
    .filter((part) => part !== '')
    .map((part) => ({ text: part, isVariable: VARIABLE_EXACT_PATTERN.test(part) }))
}

/**
 * Renders a template body with its `{{variable}}` tokens highlighted, so an
 * admin can see at a glance where the dynamic values go. Non-interactive.
 */
export const TemplateBodyPreview = ({ body, className }: { body: string; className?: string }) => {
  const segments = splitTemplateBody(body)
  return (
    <p
      className={cn(['whitespace-pre-wrap break-words text-sm text-text-secondary', className])}
      data-testid="template-body-preview"
    >
      {segments.map((segment, index) =>
        segment.isVariable ? (
          <span
            key={index}
            className="rounded bg-primary-50 px-1 font-medium text-primary"
            data-testid="template-variable"
          >
            {segment.text}
          </span>
        ) : (
          <Fragment key={index}>{segment.text}</Fragment>
        ),
      )}
    </p>
  )
}
